import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Modal, Button, Text, Textarea } from "@mantine/core";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Package,
  BookText,
} from "lucide-react";
import "./patient-details.css";
import Loading from "../loading/Loading";

const PatientDetails = () => {
  const navigate = useNavigate();
  const { patient_id } = useParams();

  const [patient, setPatient] = useState(null);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpened, setModalOpened] = useState(false);
  const [addNoteModalOpened, setAddNoteModalOpened] = useState(false);
  const [successModalOpened, setSuccessModalOpened] = useState(false);
  const [noteContent, setNoteContent] = useState("");
  const [noteError, setNoteError] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [testsPerPage, setTestsPerPage] = useState(6);
  const [pageInput, setPageInput] = useState("1");
  const [removing, setRemoving] = useState(false);

  const API_ENDPOINT = `/wp-json/dbsb/v1/patient/${patient_id}`;
  const ORDERS_API_ENDPOINT = "/wp-json/dbsb/v1/orders/list";
  const ADD_NOTE_ENDPOINT = `/wp-json/dbsb/v1/patient/${patient_id}/add-note`;

  useEffect(() => {
    const fetchPatientDetails = async () => {
      setLoading(true);
      setError("");

      try {
        if (!window.wpApiSettings?.nonce) {
          throw new Error(
            "Authentication token not found. Please try logging in again."
          );
        }

        // Fetch patient details
        const patientResponse = await fetch(API_ENDPOINT, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
          credentials: "same-origin",
        });

        if (!patientResponse.ok) {
          if (patientResponse.status === 401) {
            throw new Error(
              "You must be logged in to view patient details. Please log in and try again."
            );
          } else if (patientResponse.status === 403) {
            throw new Error("You don't have permission to view this patient.");
          } else if (patientResponse.status === 404) {
            throw new Error("Patient not found.");
          }
          throw new Error("Failed to fetch patient details. Please try again.");
        }

        const patientData = await patientResponse.json();

        // Format created_at to "15 Mar 2024"
        const createdAtDate = new Date(patientData.created_at);
        const formattedDateCreated = createdAtDate.toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });

        const patientInfo = {
          firstName: patientData.first_name || "N/A",
          lastName: patientData.last_name || "N/A",
          email: patientData.email || "N/A",
          dateCreated: formattedDateCreated,
          patientId: patientData.id || "N/A",
          dob: patientData.date_of_birth || "N/A",
          phone: patientData.phone || "N/A",
          address: patientData.address || "N/A",
          notes:
            patientData.notes?.map((note) => ({
              note: note.note_content,
              timestamp: note.note_timestamp,
            })) || [],
        };

        setPatient(patientInfo);

        // Fetch order details to get test names
        const orderIds =
          patientData.orders?.map((order) => order.order_id) || [];
        let testDetails = [];

        if (orderIds.length > 0) {
          const ordersResponse = await fetch(
            `${ORDERS_API_ENDPOINT}?patient_id=${patient_id}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                "X-WP-Nonce": window.wpApiSettings.nonce,
              },
              credentials: "same-origin",
            }
          );

          if (!ordersResponse.ok) {
            if (ordersResponse.status === 401) {
              throw new Error(
                "You must be logged in to view orders. Please log in and try again."
              );
            } else if (ordersResponse.status === 403) {
              throw new Error("You don't have permission to view orders.");
            }
            throw new Error("Failed to fetch order details. Please try again.");
          }

          const ordersData = await ordersResponse.json();

          testDetails =
            patientData.orders?.map((order) => {
              const matchingOrder = ordersData.orders.find(
                (o) => o.order_id.toString() === order.order_id.toString()
              );
              return {
                testId: order.order_id || "N/A",
                testName: matchingOrder ? matchingOrder.test_name : "Unknown",
                date: order.order_date || "N/A",
                status: order.status || "N/A",
              };
            }) || [];
        }

        setTests(testDetails);
      } catch (err) {
        console.error("Error fetching patient details:", err);
        setError(err.message);
        setPatient(null);
        setTests([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPatientDetails();
  }, [patient_id]);

  const getProfilePic = (firstName) => {
    const firstLetter = firstName?.charAt(0).toUpperCase() || "?";
    return (
      <motion.div
        className="profile-pic"
        whileHover={{ scale: 1.05 }}
        transition={{ type: "spring", stiffness: 400, damping: 10 }}
      >
        <span>{firstLetter}</span>
      </motion.div>
    );
  };

  const handleRemovePatient = async () => {
    try {
      setRemoving(true);
      if (!window.wpApiSettings?.nonce) {
        throw new Error(
          "Authentication token not found. Please try logging in again."
        );
      }

      const response = await fetch(API_ENDPOINT, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": window.wpApiSettings.nonce,
        },
        credentials: "same-origin",
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(
            "You must be logged in to remove patients. Please log in and try again."
          );
        } else if (response.status === 403) {
          throw new Error("You don't have permission to remove this patient.");
        } else if (response.status === 404) {
          throw new Error("Patient not found.");
        }
        throw new Error("Failed to remove patient. Please try again.");
      }

      setModalOpened(false);
      navigate("/manage-patients");
    } catch (err) {
      console.error("Error removing patient:", err);
      setError(err.message);
      setModalOpened(false);
    } finally {
      setRemoving(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim()) {
      setNoteError("Note content is required.");
      return;
    }

    if (noteContent.split(/\s+/).filter(Boolean).length > 50) {
      setNoteError("Note must be 50 words or less.");
      return;
    }

    setAddingNote(true);
    setNoteError("");

    try {
      if (!window.wpApiSettings?.nonce) {
        throw new Error(
          "Authentication token not found. Please try logging in again."
        );
      }

      const response = await fetch(ADD_NOTE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": window.wpApiSettings.nonce,
        },
        credentials: "same-origin",
        body: JSON.stringify({
          note_content: noteContent,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(
            "You must be logged in to add notes. Please log in and try again."
          );
        } else if (response.status === 403) {
          throw new Error("You don't have permission to add notes.");
        } else if (response.status === 404) {
          throw new Error("Patient not found.");
        }
        throw new Error("Failed to add note. Please try again.");
      }

      const data = await response.json();
      const newNote = {
        note: noteContent,
        timestamp: new Date().toISOString(),
      };

      setPatient((prev) => ({
        ...prev,
        notes: [newNote, ...prev.notes],
      }));

      setAddNoteModalOpened(false);
      setSuccessModalOpened(true);
      setNoteContent("");
    } catch (err) {
      console.error("Error adding note:", err);
      setNoteError(err.message);
    } finally {
      setAddingNote(false);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setPageInput(page.toString());
    }
  };

  const handlePageInputChange = (e) => {
    const value = e.target.value;
    setPageInput(value);
    const page = parseInt(value);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleTestsPerPageChange = (e) => {
    const value = parseInt(e.target.value);
    setTestsPerPage(value);
    setCurrentPage(1);
    setPageInput("1");
  };

  const statusClasses = {
    pending: "status-pending",
    processing: "status-processing",
    paid: "status-paid",
    scheduled: "status-scheduled",
    collected: "status-collected",
    test_in_progress: "status-test-in-progress",
    testing: "status-testing",
    in_transit: "status-in-transit",
    result_ready: "status-result-ready",
    cancelled: "status-cancelled",
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24,
      },
    },
  };

  const buttonVariants = {
    hover: { scale: 1.05, transition: { duration: 0.2 } },
    tap: { scale: 0.95, transition: { duration: 0.1 } },
  };

  const rowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    }),
    hover: {
      backgroundColor: "rgba(58, 63, 106, 0.02)",
      transition: { duration: 0.2 },
    },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
    exit: { opacity: 0, scale: 0.9, transition: { duration: 0.2 } },
  };

  const totalTests = tests.length;
  const totalPages = Math.ceil(totalTests / testsPerPage);
  const startIndex = (currentPage - 1) * testsPerPage + 1;
  const endIndex = Math.min(startIndex + testsPerPage - 1, totalTests);

  if (loading) {
    return (
      <div className="patient-details-container">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="patient-details-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <motion.div
      className="patient-details-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        className="patient-header"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="patient-info" variants={itemVariants}>
          {getProfilePic(patient.firstName)}
          <div className="patient-name-email">
            <h2>
              {patient.firstName} {patient.lastName}
            </h2>
            <span>{patient.email}</span>
          </div>
        </motion.div>
        <motion.div className="action-buttons" variants={itemVariants}>
          <motion.button
            className="add-note-button"
            onClick={() => setAddNoteModalOpened(true)}
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            Add note
          </motion.button>
          <motion.button
            className="remove-patient-button"
            onClick={() => setModalOpened(true)}
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            Remove patient
          </motion.button>
        </motion.div>
      </motion.div>

      <motion.div
        className="details-notes-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="order-details" variants={itemVariants}>
          <h3>Order details</h3>
          {[
            { label: "Date created", value: patient.dateCreated },
            { label: "Patient ID", value: patient.patientId },
            { label: "Date of birth", value: patient.dob },
            { label: "Phone number", value: patient.phone },
            { label: "Address", value: patient.address },
          ].map((item, index) => (
            <motion.div
              key={index}
              className="detail-item"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + index * 0.05, duration: 0.3 }}
              whileHover={{ x: 3 }}
            >
              <span>{item.label}</span>
              <span>{item.value}</span>
            </motion.div>
          ))}
        </motion.div>
        <motion.div className="notes" variants={itemVariants}>
          <h3>Notes</h3>
          {patient.notes.length > 0 ? (
            patient.notes.map((note, index) => (
              <motion.div
                key={index}
                className="note-entry"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
              >
                <p>{note.note}</p>
                <span className="note-meta">
                  Added on{" "}
                  {note.timestamp
                    ? new Date(note.timestamp).toLocaleString("en-US", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                        hour12: true,
                      })
                    : "N/A"}
                </span>
              </motion.div>
            ))
          ) : (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              N/A
            </motion.p>
          )}
        </motion.div>
      </motion.div>

      <motion.div
        className="tests-ordered-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div
          className="patients-title"
          initial="hidden"
          animate="visible"
        >
          <motion.img
            className="patients-icon"
            src={`${process.env.PUBLIC_URL}/assets/patient-icon.png`}
            alt="Patients"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          />
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            Tests
          </motion.h2>
        </motion.div>
        {tests.length === 0 ? (
          <motion.div
            className="no-tests"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, type: "spring", stiffness: 200 }}
          >
            <motion.img
              src={`${process.env.PUBLIC_URL}/assets/no-patients.png`}
              alt="No tests"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.5,
                delay: 0.1,
                type: "spring",
                stiffness: 200,
              }}
            />
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              No tests ordered
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              All ordered tests will appear here
            </motion.p>
          </motion.div>
        ) : (
          <>
            <motion.div
              className="tests-table"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div
                className="table-header"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <span>Test ID</span>
                <span>Test ordered</span>
                <span>Date</span>
              </motion.div>
              <motion.div
                className="table-body"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <AnimatePresence>
                  {tests
                    .slice(
                      (currentPage - 1) * testsPerPage,
                      currentPage * testsPerPage
                    )
                    .map((test, index) => (
                      <motion.div
                        key={test.testId}
                        className="table-row"
                        variants={rowVariants}
                        initial="hidden"
                        animate="visible"
                        exit={{
                          opacity: 0,
                          x: 20,
                          transition: { duration: 0.2 },
                        }}
                        whileHover="hover"
                        custom={index}
                      >
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.1 + index * 0.05 }}
                        >
                          {test.testId}
                        </motion.span>
                        <motion.span
                          className="test-name"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.3 + index * 0.05 }}
                        >
                          {test.testName}
                        </motion.span>
                        <motion.span
                          className="test-date"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.4 + index * 0.05 }}
                        >
                          {test.date}
                        </motion.span>
                        <motion.span
                          className={`status ${
                            statusClasses[test.status?.toLowerCase().trim()] ||
                            ""
                          }`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.5 + index * 0.05 }}
                        >
                          {test.status?.toUpperCase() || "N/A"}
                        </motion.span>
                        <motion.button
                          className="view-details"
                          onClick={() =>
                            navigate(`/my-order/${test.testId}`, {
                              state: {
                                fromPatientDetails: true,
                                patientId: patient_id,
                              },
                            })
                          }
                          whileHover="hover"
                          whileTap="tap"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.6 + index * 0.05 }}
                        >
                          View details
                        </motion.button>
                      </motion.div>
                    ))}
                </AnimatePresence>
              </motion.div>
            </motion.div>

            <motion.div
              className="pagination"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div className="page-selector">
                <motion.input
                  type="text"
                  value={pageInput}
                  onChange={handlePageInputChange}
                  className={parseInt(pageInput) > totalPages ? "invalid" : ""}
                  whileFocus={{
                    scale: 1.05,
                    boxShadow: "0px 0px 5px rgba(58, 63, 106, 0.2)",
                  }}
                  transition={{ duration: 0.2 }}
                />
                <span>of {totalPages} pages</span>
              </div>
              <div className="page-arrows">
                <motion.button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  variants={buttonVariants}
                  whileHover={currentPage !== 1 ? "hover" : {}}
                  whileTap={currentPage !== 1 ? "tap" : {}}
                >
                  <ChevronLeft className="arrow-button-icon" size={18} />
                </motion.button>
                <motion.button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  variants={buttonVariants}
                  whileHover={currentPage !== totalPages ? "hover" : {}}
                  whileTap={currentPage !== totalPages ? "tap" : {}}
                >
                  <ChevronRight className="arrow-button-icon" size={18} />
                </motion.button>
              </div>
              <div className="tests-per-page">
                <span>Tests per page:</span>
                <div className="select-wrapper">
                  <motion.select
                    value={testsPerPage}
                    onChange={handleTestsPerPageChange}
                    whileFocus={{ scale: 1.05 }}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    <option value={6}>6</option>
                    <option value={7}>7</option>
                    <option value={8}>8</option>
                    <option value={9}>9</option>
                    <option value={10}>10</option>
                  </motion.select>
                  <ChevronDown size={16} color="#3A3F6A" />
                </div>
              </div>
            </motion.div>
          </>
        )}
      </motion.div>

      <AnimatePresence>
        {modalOpened && (
          <Modal
            opened={modalOpened}
            onClose={() => setModalOpened(false)}
            withCloseButton={false}
            centered
            size="sm"
            className="remove-modal"
            overlayProps={{
              opacity: 0.55,
              blur: 3,
            }}
          >
            <motion.div
              className="modal-content"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <motion.h3 variants={itemVariants}>
                Are you sure you want to remove this patient?
              </motion.h3>
              <motion.div
                className="modal-buttons"
                variants={containerVariants}
              >
                <Button
                  onClick={() => setModalOpened(false)}
                  className="cancel-button"
                  component={motion.button}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  disabled={removing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRemovePatient}
                  className="remove-button"
                  component={motion.button}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  disabled={removing}
                >
                  {removing ? "Removing..." : "Remove"}
                </Button>
              </motion.div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {addNoteModalOpened && (
          <Modal
            opened={addNoteModalOpened}
            onClose={() => {
              setAddNoteModalOpened(false);
              setNoteContent("");
              setNoteError("");
            }}
            withCloseButton={false}
            centered
            size="sm"
            className="add-note-modal"
            overlayProps={{
              opacity: 0.55,
              blur: 3,
            }}
          >
            <motion.div
              className="modal-content"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <motion.h3 variants={itemVariants}>Add note</motion.h3>
              <motion.div className="form-group" variants={itemVariants}>
                <label>Note (max 50 words)</label>
                <Textarea
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  placeholder="Enter note here"
                  rows={6}
                />
                {noteError && (
                  <motion.span
                    className="error"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {noteError}
                  </motion.span>
                )}
              </motion.div>
              <motion.div
                className="modal-buttons"
                variants={containerVariants}
              >
                <Button
                  onClick={() => {
                    setAddNoteModalOpened(false);
                    setNoteContent("");
                    setNoteError("");
                  }}
                  className="cancel-button"
                  component={motion.button}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  disabled={addingNote}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddNote}
                  className="add-note-button-modal"
                  component={motion.button}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  disabled={addingNote}
                >
                  {addingNote ? "Adding..." : "Add note"}
                </Button>
              </motion.div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {successModalOpened && (
          <Modal
            opened={successModalOpened}
            onClose={() => setSuccessModalOpened(false)}
            withCloseButton={false}
            centered
            size="sm"
            className="success-modal"
            overlayProps={{
              opacity: 0.55,
              blur: 3,
            }}
          >
            <motion.div
              className="modal-content"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <img
                src={`${process.env.PUBLIC_URL}/assets/logo.svg`}
                alt="Direct by Syndicate Bio"
                className="modal-logo"
              />
              <div className="modal-message-box">
                <Text className="modal-title">Note added</Text>
                <Text className="modal-message">
                  Note has been added successfully.
                </Text>
                <Button
                  onClick={() => setSuccessModalOpened(false)}
                  className="modal-close-button patient-details-close-button"
                  component={motion.button}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  Close
                </Button>
              </div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default PatientDetails;
